import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  BarChart3, 
  Clock, 
  ArrowDownCircle,
  Activity,
  Zap
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import Hero from '../components/Hero';
import FundingRatesTable from '../components/FundingRatesTable';
import FundingRateHeatmap from '../components/FundingRateHeatmap';
import SimpleFundingRate from '../components/SimpleFundingRate';
import FundingRateAlerts from '../components/FundingRateAlerts';
import { ToastProvider } from '../components/ui/Toast';
import { fetchFundingRates } from '../lib/coinglass';
import { FundingRateSimple } from '../types/coinglass';

// Statistics component for the dashboard
const FundingRateStats: React.FC<{ symbol: string }> = ({ symbol }) => {
  const { data, isLoading } = useQuery<FundingRateSimple[]>({
    queryKey: ['fundingRates', symbol],
    queryFn: () => fetchFundingRates(symbol),
    refetchInterval: 5 * 60 * 1000,
  });

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-zinc-900 rounded-xl p-6 border border-zinc-800 animate-pulse">
            <div className="h-4 bg-zinc-800 rounded mb-2"></div>
            <div className="h-8 bg-zinc-800 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  const rates = data.map(item => item.fundingRate);
  const avgRate = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
  const maxRate = Math.max(...rates);
  const minRate = Math.min(...rates);
  const positiveRates = rates.filter(rate => rate > 0).length;
  const negativeRates = rates.filter(rate => rate < 0).length;

  const stats = [
    {
      title: 'Average Rate',
      value: `${(avgRate * 100).toFixed(4)}%`,
      color: avgRate >= 0 ? 'text-green-400' : 'text-red-400',
      icon: Activity,
      description: avgRate >= 0 ? 'Longs paying shorts' : 'Shorts paying longs'
    },
    {
      title: 'Highest Rate',
      value: `${(maxRate * 100).toFixed(4)}%`,
      color: 'text-green-400',
      icon: TrendingUp,
      description: `${data.find(item => item.fundingRate === maxRate)?.exchangeName}`
    },
    {
      title: 'Lowest Rate',
      value: `${(minRate * 100).toFixed(4)}%`,
      color: 'text-red-400',
      icon: ArrowDownCircle,
      description: `${data.find(item => item.fundingRate === minRate)?.exchangeName}`
    },
    {
      title: 'Market Sentiment',
      value: `${((positiveRates / (positiveRates + negativeRates)) * 100).toFixed(1)}%`,
      color: positiveRates > negativeRates ? 'text-green-400' : 'text-red-400',
      icon: BarChart3,
      description: `${positiveRates} pos / ${negativeRates} neg`
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {stats.map((stat, index) => (
        <motion.div
          key={stat.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-zinc-900 rounded-xl p-6 border border-zinc-800 hover:border-zinc-700 transition-colors"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-zinc-800 rounded-lg">
              <stat.icon className="w-5 h-5 text-yellow-400" />
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-gray-400">{stat.title}</p>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-gray-500">{stat.description}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

const CryptoSelector: React.FC<{ 
  selectedSymbol: string; 
  onSymbolChange: (symbol: string) => void 
}> = ({ selectedSymbol, onSymbolChange }) => {
  const cryptos = [
    { symbol: 'BTC', name: 'Bitcoin' },
    { symbol: 'ETH', name: 'Ethereum' },
    { symbol: 'ADA', name: 'Cardano' },
    { symbol: 'MATIC', name: 'Polygon' },
    { symbol: 'SOL', name: 'Solana' },
    { symbol: 'AVAX', name: 'Avalanche' }
  ];

  return (
    <div className="mb-8">
      <div className="flex flex-wrap gap-2">
        {cryptos.map((crypto) => (
          <button
            key={crypto.symbol}
            onClick={() => onSymbolChange(crypto.symbol)}
            className={`px-4 py-2 rounded-lg transition-colors font-medium ${
              selectedSymbol === crypto.symbol
                ? 'bg-yellow-400 text-black'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            {crypto.symbol}
          </button>
        ))}
      </div>
    </div>
  );
};

const FundingRatesDashboard: React.FC = () => {
  const [selectedSymbol, setSelectedSymbol] = useState('BTC');

  useEffect(() => {
    document.title = `${selectedSymbol} Funding Rates Dashboard | CryptoIndia`;
  }, [selectedSymbol]);

  return (
    <div className="min-h-screen bg-black">
      <Hero 
        title="Crypto Funding Rates Dashboard"
        subtitle="Real-time funding rate data from multiple exchanges for comprehensive market analysis"
        config={{
          image: "https://images.pexels.com/photos/6770610/pexels-photo-6770610.jpeg",
          overlayOpacity: 0.8,
          position: "center"
        }}
      />

      <ToastProvider>
        <div className="container mx-auto px-4 py-12">
          {/* Crypto Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Select Cryptocurrency
            </h2>
            <CryptoSelector 
              selectedSymbol={selectedSymbol} 
              onSymbolChange={setSelectedSymbol}
            />
          </motion.div>

          {/* Statistics Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <FundingRateStats symbol={selectedSymbol} />
          </motion.div>

          {/* Main Dashboard Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-8"
          >            {/* Quick Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <SimpleFundingRate symbol={selectedSymbol} />
              <FundingRateAlerts symbol={selectedSymbol} />
            </div>

            {/* Quick Info Card */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-zinc-800 rounded-lg">
                  <Zap className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Quick Info</h3>
                  <p className="text-sm text-gray-400">Key funding rate insights</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Funding Interval</span>
                  <span className="text-white">8 hours</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Next Funding</span>
                  <span className="text-white flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    ~2h 30m
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Market Impact</span>
                  <span className="text-yellow-400">Medium</span>
                </div>
              </div>
            </div>

            {/* Heatmap */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Funding Rate Heatmap</h3>
              <FundingRateHeatmap />
            </div>

            {/* Detailed Table */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Detailed Funding Rates</h3>
              <FundingRatesTable symbol={selectedSymbol} />
            </div>

            {/* Educational Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-xl p-8 border border-zinc-700"
            >
              <h3 className="text-xl font-semibold text-white mb-4">Understanding Funding Rates</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-medium text-yellow-400 mb-2">What are Funding Rates?</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Funding rates are periodic payments between long and short traders in perpetual futures markets. 
                    They help keep the perpetual contract price close to the spot price.
                  </p>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-yellow-400 mb-2">How to Interpret</h4>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• <span className="text-green-400">Positive rates</span>: Longs pay shorts (bullish sentiment)</li>
                    <li>• <span className="text-red-400">Negative rates</span>: Shorts pay longs (bearish sentiment)</li>
                    <li>• Higher absolute values indicate stronger market bias</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </ToastProvider>
    </div>
  );
};

export default FundingRatesDashboard;
